import { CaretRightOutlined, InfoCircleOutlined, LineChartOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

function StatHint({ title }) {
    return (
        <span className="admin-stats-panel-hint-wrap" title={title} aria-label={title}>
            <InfoCircleOutlined className="admin-stats-panel-hint" />
        </span>
    );
}

StatHint.propTypes = {
    title: PropTypes.string.isRequired
};

const formatShare = (count, total) => (total > 0 ? `${((count / total) * 100).toFixed(1)}%` : '0%');

function SegmentBar({ segments, total }) {
    const visibleSegments = segments.filter((segment) => segment.count > 0);

    if (total <= 0 || visibleSegments.length === 0) {
        return (
            <div className="admin-stats-segment-bar admin-stats-segment-bar--empty">
                No accounts in scope
            </div>
        );
    }

    return (
        <div
            className="admin-stats-segment-bar"
            role="img"
            aria-label="Account status distribution"
        >
            {visibleSegments.map((segment) => (
                <div
                    key={segment.key}
                    className="admin-stats-segment-bar__slice"
                    title={`${segment.label}: ${segment.count} (${formatShare(segment.count, total)})`}
                    style={{
                        flexGrow: segment.count,
                        backgroundColor: segment.color
                    }}
                />
            ))}
        </div>
    );
}

SegmentBar.propTypes = {
    segments: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            count: PropTypes.number.isRequired,
            color: PropTypes.string.isRequired
        })
    ).isRequired,
    total: PropTypes.number.isRequired
};

function BreakdownRow({
    label,
    count,
    total,
    color,
    indent = 0,
    hint,
    valueStyle,
    compact = false,
    trailing = null
}) {
    return (
        <div
            className={`admin-stats-breakdown-row${compact ? ' admin-stats-breakdown-row--compact' : ''}`}
            style={{ paddingLeft: indent ? 10 : 0 }}
        >
            <div className="admin-stats-breakdown-row__label">
                {color && (
                    <span
                        className="admin-stats-breakdown-row__dot"
                        style={{ backgroundColor: color }}
                    />
                )}
                <span>{label}</span>
                {hint && <StatHint title={hint} />}
                <span className="admin-stats-breakdown-row__trailing" aria-hidden={!trailing}>
                    {trailing}
                </span>
            </div>
            <span className="admin-stats-breakdown-row__count" style={valueStyle}>
                {count}
            </span>
            <span className="admin-stats-breakdown-row__share">{formatShare(count, total)}</span>
        </div>
    );
}

BreakdownRow.propTypes = {
    label: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    color: PropTypes.string,
    indent: PropTypes.number,
    hint: PropTypes.string,
    valueStyle: PropTypes.object,
    compact: PropTypes.bool,
    trailing: PropTypes.node
};

function CollapsibleBreakdownGroup({ title, hint, color, total, groupTotal, rows, ariaLabel }) {
    const [expanded, setExpanded] = useState(false);
    const chevron = (
        <CaretRightOutlined
            className={`admin-stats-breakdown-row__chevron${expanded ? ' admin-stats-breakdown-row__chevron--expanded' : ''}`}
        />
    );

    return (
        <div className="admin-stats-breakdown-group">
            <button
                type="button"
                className="admin-stats-breakdown-row admin-stats-breakdown-row--compact admin-stats-breakdown-row--toggle"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
                aria-label={ariaLabel}
            >
                <div className="admin-stats-breakdown-row__label">
                    <span
                        className="admin-stats-breakdown-row__dot"
                        style={{ backgroundColor: color }}
                    />
                    <span>{title}</span>
                    {hint && <StatHint title={hint} />}
                    <span className="admin-stats-breakdown-row__trailing" aria-hidden={false}>
                        {chevron}
                    </span>
                </div>
                <span className="admin-stats-breakdown-row__count">{groupTotal}</span>
                <span className="admin-stats-breakdown-row__share">
                    {formatShare(groupTotal, total)}
                </span>
            </button>
            {expanded && (
                <div className="admin-stats-breakdown-group__children">
                    {rows.map((row) => (
                        <BreakdownRow
                            key={row.key}
                            label={row.subLabel || row.label}
                            count={row.count}
                            total={total}
                            color={row.color}
                            hint={row.hint}
                            indent={1}
                            trailing={
                                <span className="admin-stats-breakdown-row__chevron-placeholder" />
                            }
                            compact
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

CollapsibleBreakdownGroup.propTypes = {
    title: PropTypes.string.isRequired,
    hint: PropTypes.string,
    color: PropTypes.string.isRequired,
    total: PropTypes.number.isRequired,
    groupTotal: PropTypes.number.isRequired,
    rows: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            subLabel: PropTypes.string,
            count: PropTypes.number.isRequired,
            color: PropTypes.string.isRequired,
            hint: PropTypes.string
        })
    ).isRequired,
    ariaLabel: PropTypes.string.isRequired
};

function MetricRow({ label, value, detail, hint, valueStyle, emphasize, labelAction = null }) {
    return (
        <div
            className={`admin-stats-metric-row${emphasize ? ' admin-stats-metric-row--emphasize' : ''}`}
        >
            <div className="admin-stats-metric-row__label">
                <span>{label}</span>
                {hint && <StatHint title={hint} />}
                {labelAction}
            </div>
            <span className="admin-stats-metric-row__value" style={valueStyle}>
                <span className="admin-stats-metric-row__value-primary">{value}</span>
                {detail && <span className="admin-stats-metric-row__value-detail">{detail}</span>}
            </span>
        </div>
    );
}

function MetricGroup({ title, children }) {
    return (
        <div className="admin-stats-metric-group">
            {title && <span className="admin-stats-metric-group__title">{title}</span>}
            <div className="admin-stats-metric-group__rows">{children}</div>
        </div>
    );
}

MetricGroup.propTypes = {
    title: PropTypes.string,
    children: PropTypes.node.isRequired
};

MetricRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    detail: PropTypes.string,
    hint: PropTypes.string,
    valueStyle: PropTypes.object,
    emphasize: PropTypes.bool,
    labelAction: PropTypes.node
};

function StatsPanel({ title, hint, children, accent = '#2962ff' }) {
    return (
        <div className="admin-stats-panel" style={{ '--panel-accent': accent }}>
            <div className="admin-stats-panel__header">
                <span className="admin-stats-panel__title">{title}</span>
                {hint && <StatHint title={hint} />}
            </div>
            <div className="admin-stats-panel__body">{children}</div>
        </div>
    );
}

StatsPanel.propTypes = {
    title: PropTypes.string.isRequired,
    hint: PropTypes.string,
    children: PropTypes.node.isRequired,
    accent: PropTypes.string
};

export default function AdminStatsPanel({
    totalAccounts,
    barSegments,
    accountBreakdownOpenRows,
    accountBreakdownOpenTotal,
    accountBreakdownClosedRows,
    accountBreakdownClosedTotal,
    uncategorizedCount,
    profitability,
    payouts,
    onOpenGrossLiabilityChart
}) {
    const {
        openProfitableEligible,
        openProfitableOfAllOpenPercent,
        openProfitableOfEligibleOpenPercent,
        openEligibleTotal,
        paidTotal,
        closedTotal,
        closedSurvivalRate,
        programPaidTotal,
        programClosedTotal,
        programSurvivalRate,
        openTotal
    } = profitability;

    return (
        <div className="admin-stats-panel-grid">
            <StatsPanel
                title="Accounts"
                hint="All accounts in scope, grouped by lifecycle status."
                accent="#2962ff"
            >
                <div className="admin-stats-hero-row">
                    <div className="admin-stats-hero-metric">
                        <span className="admin-stats-hero-metric__label">Total</span>
                        <span className="admin-stats-hero-metric__value">{totalAccounts}</span>
                    </div>
                    <SegmentBar segments={barSegments} total={totalAccounts} />
                </div>

                <div className="admin-stats-breakdown">
                    <CollapsibleBreakdownGroup
                        title="Open"
                        hint="Active accounts without a closed status or workflow state."
                        color="#2962FF"
                        total={totalAccounts}
                        groupTotal={accountBreakdownOpenTotal}
                        rows={accountBreakdownOpenRows}
                        ariaLabel={`Open accounts, ${accountBreakdownOpenTotal} total`}
                    />
                    <CollapsibleBreakdownGroup
                        title="Closed"
                        hint="All non-open accounts in scope, grouped by closed status."
                        color="#64748b"
                        total={totalAccounts}
                        groupTotal={accountBreakdownClosedTotal}
                        rows={accountBreakdownClosedRows}
                        ariaLabel={`Closed accounts, ${accountBreakdownClosedTotal} total`}
                    />
                </div>

                {uncategorizedCount > 0 && (
                    <span className="admin-stats-footnote">
                        {uncategorizedCount} unmapped account(s)
                    </span>
                )}
            </StatsPanel>

            <StatsPanel
                title="Profitability"
                hint="Profitable share within open and closed populations in scope."
                accent="#389e0d"
            >
                <MetricGroup title="Paid">
                    <MetricRow
                        label="Program Survival Rate"
                        value={programSurvivalRate}
                        detail={`(${programPaidTotal}/${programClosedTotal})`}
                        hint="Paid / closed accounts whose program expiration has passed (excludes accounts still in their 90-day window)."
                        emphasize
                    />
                    <MetricRow
                        label="Closed Survival Rate"
                        value={closedSurvivalRate}
                        detail={`(${paidTotal}/${closedTotal})`}
                        hint="Paid / all closed accounts in scope."
                    />
                </MetricGroup>
                <MetricGroup title="Open">
                    <MetricRow
                        label="Profitable & Eligible / All Open"
                        value={openProfitableOfAllOpenPercent}
                        detail={`(${openProfitableEligible}/${openTotal})`}
                        hint="Profitable and payout-eligible open accounts / all open accounts."
                    />
                    <MetricRow
                        label="Profitable & Eligible / Open Eligible"
                        value={openProfitableOfEligibleOpenPercent}
                        detail={`(${openProfitableEligible}/${openEligibleTotal})`}
                        hint="Profitable and payout-eligible open accounts / open accounts that are payout eligible."
                    />
                </MetricGroup>
            </StatsPanel>

            <StatsPanel
                title="Payouts"
                hint="Paid history and open liability ($10k cap)."
                accent="#d4380d"
            >
                <div className="admin-stats-metric-columns">
                    <MetricGroup title="Paid">
                        <MetricRow label="To date" value={payouts.totalPaid} emphasize />
                        <MetricRow label="Last 90 days" value={payouts.paidLast90Days} />
                        <MetricRow
                            label="Avg Payout (Closed)"
                            value={payouts.avgClosed}
                            hint="Total paid payouts divided by all closed (non-open) workflow accounts in scope, including winners, losers, and archived accounts."
                        />
                        <MetricRow
                            label="Avg Payout (Paid Only)"
                            value={payouts.avgPaid}
                            hint="Total paid payouts divided by Paid workflow accounts in scope."
                        />
                    </MetricGroup>
                    <MetricGroup title="Liability & forecast">
                        <MetricRow
                            label="Gross Potential Liability"
                            value={payouts.grossLiability}
                            valueStyle={{ color: '#cf1322' }}
                            emphasize
                            labelAction={
                                onOpenGrossLiabilityChart ? (
                                    <button
                                        type="button"
                                        className="admin-stats-metric-row__chart-btn"
                                        onClick={onOpenGrossLiabilityChart}
                                        title="View liability over time"
                                        aria-label="View gross potential liability chart"
                                    >
                                        <LineChartOutlined />
                                    </button>
                                ) : null
                            }
                        />
                        <MetricRow
                            label="Avg Payout (Open)"
                            value={payouts.avgOpen}
                            valueStyle={{ color: '#cf1322' }}
                            hint="Gross potential liability divided by total active open accounts in scope."
                        />
                        <MetricRow
                            label="30d cash need"
                            value={payouts.next30Cash}
                            valueStyle={{ color: '#cf1322' }}
                        />
                        <MetricRow label="30d ending accts" value={payouts.next30Accounts} />
                    </MetricGroup>
                </div>
            </StatsPanel>
        </div>
    );
}

AdminStatsPanel.propTypes = {
    totalAccounts: PropTypes.number.isRequired,
    barSegments: PropTypes.array.isRequired,
    accountBreakdownOpenRows: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            subLabel: PropTypes.string,
            count: PropTypes.number.isRequired,
            color: PropTypes.string.isRequired,
            hint: PropTypes.string
        })
    ).isRequired,
    accountBreakdownOpenTotal: PropTypes.number.isRequired,
    accountBreakdownClosedRows: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            subLabel: PropTypes.string,
            count: PropTypes.number.isRequired,
            color: PropTypes.string.isRequired,
            hint: PropTypes.string
        })
    ).isRequired,
    accountBreakdownClosedTotal: PropTypes.number.isRequired,
    uncategorizedCount: PropTypes.number.isRequired,
    profitability: PropTypes.shape({
        openTotal: PropTypes.number.isRequired,
        openEligibleTotal: PropTypes.number.isRequired,
        openProfitableEligible: PropTypes.number.isRequired,
        openProfitableOfAllOpenPercent: PropTypes.string.isRequired,
        openProfitableOfEligibleOpenPercent: PropTypes.string.isRequired,
        paidTotal: PropTypes.number.isRequired,
        closedTotal: PropTypes.number.isRequired,
        closedSurvivalRate: PropTypes.string.isRequired,
        programPaidTotal: PropTypes.number.isRequired,
        programClosedTotal: PropTypes.number.isRequired,
        programSurvivalRate: PropTypes.string.isRequired
    }).isRequired,
    payouts: PropTypes.shape({
        totalPaid: PropTypes.string.isRequired,
        paidLast90Days: PropTypes.string.isRequired,
        grossLiability: PropTypes.string.isRequired,
        avgClosed: PropTypes.string.isRequired,
        avgPaid: PropTypes.string.isRequired,
        avgOpen: PropTypes.string.isRequired,
        next30Cash: PropTypes.string.isRequired,
        next30Accounts: PropTypes.number.isRequired
    }).isRequired,
    onOpenGrossLiabilityChart: PropTypes.func
};
